import React, { useState } from 'react';
import { AlertTriangle, CheckCircle2, Clock, ListChecks, Lock, Plus, Scale, Unlock } from 'lucide-react';
import { readOnlyTitle } from '../../services/readOnlyNotice';
import type { FolgeAuswahlZeile } from '../../services/folgeunterlagen/folgeunterlagen';
import type { FolgeArt } from '../../services/folgeunterlagen/typen';
import { FOLGE_FUSS_SATZ, abschlussRueckfrage, gewaehlteArten, istGewaehlt, type FolgeHaken } from './folgeunterlagenAnzeige';
import { ABSCHLUSS_UNGESPEICHERT, abschlussNachfrageOffen, type VerlassenLeisteText } from './leitfadenRahmen';
import { HINWEIS_ART_LABEL } from './planungLogic';
import type { FreigabePruefung, Planungsfrage, Planungshinweis } from './types';

/**
 * Bausteine der Planungsphase, die mehr als eine Ansicht braucht: die Reiter
 * (`PlanungReiter.tsx`) und ab Stufe L2 die Stationen des Leitfadens. Das
 * Markup stammt aus `PlanungModul.tsx` (neu die `VerlassenLeiste` und die
 * Abschluss-Rueckfrage im `FreigabeKasten`); Zustand und Handler bleiben
 * dort, hier kommt alles ueber Props herein.
 *
 * Die Sichtbarkeitsbedingung „nicht abgeschlossen" der beiden Banner haelt der
 * Aufrufer: er uebergibt `anzahl: 0` bzw. `fragen: []`, dann zeichnen sie nichts.
 */

/** Abgelaufene Fristen ohne Antwort — mit Inline-Rueckfrage vor dem
 *  Feststellen (kein Browser-Dialog). „Im Katalog ansehen" nur mit `onAnsehen`. */
export const FristenBanner: React.FC<{
    anzahl: number;
    nachfrage: boolean;
    isReadOnly: boolean;
    laeuft: boolean;
    onNachfrage: (offen: boolean) => void;
    onFeststellen: () => void;
    onAnsehen?: () => void;
}> = ({ anzahl, nachfrage, isReadOnly, laeuft, onNachfrage, onFeststellen, onAnsehen }) => {
    if (anzahl === 0) return null;
    return (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-900 flex items-center gap-3 flex-wrap">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
            <span className="flex-1 min-w-[16rem]">
                Bei {anzahl} Frage(n) ist die Frist abgelaufen, ohne dass eine Antwort eingegangen ist.
                {' '}Der Status „nicht beantwortet" wird nicht von selbst gesetzt; er ist Ihre Feststellung.
            </span>
            {nachfrage ? (
                <span className="inline-flex items-center gap-2 text-xs">
                    <span className="font-bold">{anzahl} Frage(n) auf „nicht beantwortet" setzen?</span>
                    <button
                        type="button"
                        onClick={() => onFeststellen()}
                        disabled={laeuft}
                        className="px-3 py-1.5 font-bold text-white bg-amber-700 rounded-lg hover:bg-amber-800 disabled:opacity-40"
                    >
                        Ja, feststellen
                    </button>
                    <button
                        type="button"
                        onClick={() => onNachfrage(false)}
                        className="font-bold underline"
                    >
                        Nein
                    </button>
                </span>
            ) : (
                <button
                    type="button"
                    onClick={() => onNachfrage(true)}
                    disabled={isReadOnly || laeuft}
                    title={isReadOnly ? readOnlyTitle('Feststellen') : undefined}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-amber-900 bg-white border border-amber-300 rounded-lg hover:bg-amber-100 disabled:opacity-40"
                >
                    <Clock className="w-3.5 h-3.5" /> Abgelaufene Fristen feststellen
                </button>
            )}
            {onAnsehen && (
                <button
                    type="button"
                    onClick={() => onAnsehen()}
                    className="text-xs font-bold underline"
                >
                    Im Katalog ansehen
                </button>
            )}
        </div>
    );
};

/**
 * Verlassen mit ungespeicherten Grundlagen oder waehrend einer KI-Auswertung
 * (Plan 2.2) — Inline-Leiste unter dem Kopf statt Browser-Dialog, beide
 * Ansichten, fuer jedes Ziel (Projekt oder Folgemodul). Sie klebt oben im
 * Scrollbereich des Moduls: „Zum Projekt" sitzt auch in der Fussleiste einer
 * Station, und die Frage muss dort zu sehen sein, wo man gerade steht.
 * Satz und Knoepfe kommen aus `verlassenLeisteText`; ohne `text.speichern`
 * (nur die KI laeuft) gibt es nichts zu speichern.
 */
export const VerlassenLeiste: React.FC<{
    text: VerlassenLeisteText;
    laeuft: boolean;
    onSpeichern: () => void;
    onVerwerfen: () => void;
    onBleiben: () => void;
}> = ({ text, laeuft, onSpeichern, onVerwerfen, onBleiben }) => (
    <div role="alert" className="sticky top-0 z-20 mb-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-900 flex items-center gap-3 flex-wrap shadow-sm">
        <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
        <span className="flex-1 min-w-[16rem] font-bold">{text.satz}</span>
        {text.speichern && (
            <button
                type="button"
                onClick={() => onSpeichern()}
                disabled={laeuft}
                className="px-3 py-1.5 text-xs font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40"
            >
                {text.speichern}
            </button>
        )}
        <button
            type="button"
            onClick={() => onVerwerfen()}
            disabled={laeuft}
            className="px-3 py-1.5 text-xs font-bold text-amber-900 bg-white border border-amber-300 rounded-lg hover:bg-amber-100 disabled:opacity-40"
        >
            {text.gehen}
        </button>
        <button
            type="button"
            onClick={() => onBleiben()}
            className="text-xs font-bold underline"
        >
            Hierbleiben
        </button>
    </div>
);

/** Sammel-Knopf „gegenstandslos" (Katalogfassung 3): Fragen, die nach
 *  den GESPEICHERTEN Merkmalen nicht mehr ausgeloest wuerden und noch
 *  unberuehrt sind. Nie von selbst — eine Rueckfrage, dann schreiben. */
export const GegenstandslosBanner: React.FC<{
    fragen: Planungsfrage[];
    nachfrage: boolean;
    isReadOnly: boolean;
    laeuft: boolean;
    onNachfrage: (offen: boolean) => void;
    onSetzen: () => void;
}> = ({ fragen, nachfrage, isReadOnly, laeuft, onNachfrage, onSetzen }) => {
    if (!fragen.length) return null;
    return (
        <div className="mb-4 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-800 flex items-center gap-3 flex-wrap">
            <ListChecks className="w-4 h-4 text-slate-500 shrink-0" />
            <span className="flex-1 min-w-[16rem]">
                {fragen.length} offene Frage(n) treffen nach den gespeicherten Projektmerkmalen
                nicht mehr zu ({fragen.slice(0, 6).map(f => f.code).join(', ')}
                {fragen.length > 6 ? ' …' : ''}). Sie wurden noch nicht versendet.
            </span>
            {nachfrage ? (
                <span className="inline-flex items-center gap-2 text-xs">
                    <span className="font-bold">{fragen.length} Frage(n) auf „gegenstandslos" setzen? Die Begründung kommt in die interne Bewertung.</span>
                    <button
                        type="button"
                        onClick={() => onSetzen()}
                        disabled={laeuft}
                        className="px-3 py-1.5 font-bold text-white bg-slate-700 rounded-lg hover:bg-slate-800 disabled:opacity-40"
                    >
                        Ja, setzen
                    </button>
                    <button
                        type="button"
                        onClick={() => onNachfrage(false)}
                        className="font-bold underline"
                    >
                        Nein
                    </button>
                </span>
            ) : (
                <button
                    type="button"
                    onClick={() => onNachfrage(true)}
                    disabled={isReadOnly || laeuft}
                    title={isReadOnly ? readOnlyTitle('Gegenstandslos setzen') : 'Nicht versendete Fragen ohne Antwort, deren Merkmal ausdrücklich verneint ist'}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-100 disabled:opacity-40"
                >
                    Als „gegenstandslos" markieren
                </button>
            )}
        </div>
    );
};

/**
 * Das Freigabe-Gate unter der Fragenliste: „Abschließen" bzw. „Wieder öffnen".
 *
 * „Abschließen" fragt inline nach (kein Browser-Dialog, Muster der Banner;
 * offen haelt der Container): Restrisiko-Satz und je Folgeunterlage ein
 * Haken „als Entwurf anlegen" — vorgewaehlt nach `folgeAuswahl`, nicht
 * waehlbare ausgegraut mit ihrem Grund. Liest der Container den Bestand noch,
 * steht „wird geprüft …" und Abschliessen geht trotzdem (dann ohne Anlage).
 * Eigener Zustand sind nur die angeklickten Haken.
 *
 * Mit ungespeicherten Grundlagen (`ungespeichert`) zeigt die Rueckfrage statt
 * der Haken den Satz `ABSCHLUSS_UNGESPEICHERT` und „Grundlagen speichern":
 * abgeschlossen wird der gespeicherte Stand, und in der abgeschlossenen Phase
 * liesse sich der Entwurf nicht mehr speichern. Nach dem Speichern steht die
 * Rueckfrage mit den Haken da.
 */
export const FreigabeKasten: React.FC<{
    freigabe: FreigabePruefung;
    abgeschlossen: boolean;
    isReadOnly: boolean;
    laeuft: boolean;
    /** Die Inline-Rueckfrage vor dem Abschluss ist offen. */
    nachfrage: boolean;
    /** Punkte, die als Restrisikoliste in die Ausfuehrungsphase gehen. */
    restrisiken: number;
    /** Die Auswahl der Folgeunterlagen (`folgeAuswahl`). */
    folgeZeilen: FolgeAuswahlZeile[];
    /** Die Grundlagen haben ungespeicherte Angaben — erst speichern. */
    ungespeichert: boolean;
    onNachfrage: (offen: boolean) => void;
    /** Abschliessen; danach die gewaehlten Folgeunterlagen als Entwurf. */
    onFreigeben: (arten: FolgeArt[]) => void;
    /** „Grundlagen speichern" aus der Rueckfrage (derselbe Weg wie der Speichern-Knopf). */
    onGrundlagenSpeichern: () => void;
    onWiedereroeffnen: () => void;
}> = ({
    freigabe, abgeschlossen, isReadOnly, laeuft, nachfrage, restrisiken, folgeZeilen, ungespeichert,
    onNachfrage, onFreigeben, onGrundlagenSpeichern, onWiedereroeffnen,
}) => {
    const [haken, setHaken] = useState<FolgeHaken>({});
    const rueckfrage = abschlussRueckfrage(restrisiken);
    const fragtNach = abschlussNachfrageOffen({ nachfrage, freigabeMoeglich: freigabe.freigabeMoeglich, abgeschlossen });
    const abbrechen = () => { setHaken({}); onNachfrage(false); };
    return (
        <div className={`rounded-2xl border p-5 ${
            freigabe.freigabeMoeglich
                ? 'bg-emerald-50 border-emerald-200'
                : 'bg-amber-50 border-amber-200'
        }`}>
            <div className="flex items-start gap-3">
                {freigabe.freigabeMoeglich
                    ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                    : <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />}
                <div className="min-w-0 flex-1">
                    <h3 className="font-bold text-slate-800 text-sm mb-1">Abschluss der Planungsphase</h3>
                    <p className="text-xs text-slate-600 leading-snug">{freigabe.hinweis}</p>
                    {freigabe.blockierendeCodes.length > 0 && (
                        <p className="text-[11px] font-mono text-amber-800 mt-2 break-words">
                            {freigabe.blockierendeCodes.join(' · ')}
                        </p>
                    )}
                    {freigabe.gedeckteCodes.length > 0 && (
                        <p className="text-[11px] text-slate-500 mt-2">
                            Durch Bedenkenanzeige gedeckt: <span className="font-mono">{freigabe.gedeckteCodes.join(' · ')}</span>
                        </p>
                    )}
                </div>
                {!fragtNach && (
                    <div className="shrink-0">
                        {abgeschlossen ? (
                            <button
                                type="button"
                                onClick={() => onWiedereroeffnen()}
                                disabled={isReadOnly || laeuft}
                                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-40"
                            >
                                <Unlock className="w-3.5 h-3.5" /> Wieder öffnen
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={() => onNachfrage(true)}
                                disabled={isReadOnly || laeuft || !freigabe.freigabeMoeglich}
                                title={isReadOnly ? readOnlyTitle('Abschließen') : undefined}
                                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-40"
                            >
                                <Lock className="w-3.5 h-3.5" /> Abschließen
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* ─── Erst speichern: statt der Haken Satz und Knopf ─────── */}
            {fragtNach && ungespeichert && (
                <div className="mt-4 rounded-xl border border-amber-200 bg-white px-4 py-3 text-sm text-slate-800">
                    <p className="font-bold flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                        <span>{ABSCHLUSS_UNGESPEICHERT}</span>
                    </p>
                    <div className="mt-3 flex items-center gap-3">
                        <button
                            type="button"
                            onClick={() => onGrundlagenSpeichern()}
                            disabled={isReadOnly || laeuft}
                            className="px-3 py-1.5 text-xs font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40"
                        >
                            Jetzt speichern
                        </button>
                        <button
                            type="button"
                            onClick={abbrechen}
                            className="text-xs font-bold text-slate-600 underline"
                        >
                            Abbrechen
                        </button>
                    </div>
                </div>
            )}

            {/* ─── Inline-Rueckfrage (statt window.confirm) ──────────── */}
            {fragtNach && !ungespeichert && (
                <div className="mt-4 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800">
                    <p className="font-bold">{rueckfrage.frage}</p>
                    {rueckfrage.rest && <p className="mt-0.5 text-xs text-slate-600 leading-snug">{rueckfrage.rest}</p>}
                    {folgeZeilen.length > 0 && (
                        <fieldset className="mt-3">
                            <legend className="text-xs font-bold text-slate-700">Folgeunterlagen als Entwurf anlegen:</legend>
                            <ul className="mt-1.5 space-y-1">
                                {folgeZeilen.map(z => (
                                    <li key={z.art}>
                                        <label className={`flex items-start gap-2 text-xs ${z.waehlbar ? 'text-slate-700' : 'text-slate-400'}`}>
                                            <input
                                                type="checkbox"
                                                checked={istGewaehlt(z, haken)}
                                                disabled={!z.waehlbar || laeuft}
                                                onChange={e => {
                                                    const an = e.target.checked;
                                                    setHaken(h => ({ ...h, [z.art]: an }));
                                                }}
                                                className="mt-0.5 w-3.5 h-3.5 accent-slate-700"
                                            />
                                            <span className="min-w-0">
                                                <span className="font-bold">{z.titel}</span>
                                                {': '}
                                                <span title={z.waehlbar ? (z.begruendung || undefined) : undefined}>
                                                    {z.waehlbar ? z.grund : z.sperrgrund}
                                                </span>
                                            </span>
                                        </label>
                                    </li>
                                ))}
                            </ul>
                            <p className="mt-1.5 text-[11px] text-slate-500 leading-snug">{FOLGE_FUSS_SATZ}</p>
                        </fieldset>
                    )}
                    <div className="mt-3 flex items-center gap-3">
                        <button
                            type="button"
                            onClick={() => onFreigeben(gewaehlteArten(folgeZeilen, haken))}
                            disabled={isReadOnly || laeuft}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-slate-800 rounded-lg hover:bg-slate-900 disabled:opacity-40"
                        >
                            <Lock className="w-3.5 h-3.5" /> Abschließen
                        </button>
                        <button
                            type="button"
                            onClick={abbrechen}
                            className="text-xs font-bold text-slate-600 underline"
                        >
                            Abbrechen
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

/** Der Inhalt des Reiters „Bedenken": Erklaerzeile mit „Neu anlegen",
 *  Leerzustand bzw. die Liste der Hinweise und Bedenkenanzeigen. */
export const HinweisListe: React.FC<{
    hinweise: Planungshinweis[];
    isReadOnly: boolean;
    laeuft: boolean;
    onNeu: () => void;
    onOeffnen: (h: Planungshinweis) => void;
}> = ({ hinweise, isReadOnly, laeuft, onNeu, onOeffnen }) => (
    <div className="space-y-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-3 flex-wrap">
            <p className="text-xs text-slate-500 flex-1 min-w-0">
                Nur eine <strong>versendete</strong> Bedenkenanzeige deckt eine offene haftungsrelevante
                Frage. Ein Entwurf tut das nicht.
            </p>
            <button
                type="button"
                onClick={() => onNeu()}
                disabled={isReadOnly || laeuft}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40"
            >
                <Plus className="w-3.5 h-3.5" /> Neu anlegen
            </button>
        </div>

        {hinweise.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
                <Scale className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">Noch keine Hinweise oder Bedenkenanzeigen.</p>
            </div>
        ) : (
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                {hinweise.map(h => (
                    <button
                        key={h.id}
                        type="button"
                        onClick={() => onOeffnen(h)}
                        className="w-full text-left px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors flex items-start gap-3"
                    >
                        <span className={`shrink-0 text-[10px] font-black px-2 py-1 rounded border ${
                            h.art === 'gefahr' ? 'bg-rose-50 text-rose-700 border-rose-200'
                                : h.art === 'bedenken' ? 'bg-amber-50 text-amber-700 border-amber-200'
                                    : 'bg-slate-100 text-slate-500 border-slate-200'
                        }`}>
                            Nr. {h.nummer}
                        </span>
                        <div className="min-w-0 flex-1">
                            <div className="text-sm font-bold text-slate-800">{h.betreff || '(ohne Betreff)'}</div>
                            <div className="text-[11px] text-slate-500">
                                {HINWEIS_ART_LABEL[h.art]} · {h.empfaenger_name ? `an ${h.empfaenger_name}` : 'Empfänger offen'}
                                {h.frage_codes.length > 0 && <> · {h.frage_codes.length} Frage(n)</>}
                            </div>
                        </div>
                        <span className={`shrink-0 text-[11px] font-bold ${h.versendet_am ? 'text-emerald-600' : 'text-slate-400'}`}>
                            {h.versendet_am ? `versendet ${String(h.versendet_am).slice(0, 10)}` : 'Entwurf'}
                        </span>
                    </button>
                ))}
            </div>
        )}
    </div>
);
